import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, LogOut, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function useNotifications() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const markRead = () => api.post("/notifications/read").then(load).catch(() => {});
  return { items, unread: items.filter((i) => !i.read).length, markRead, reload: load };
}

export default function DashboardShell({ title, subtitle, tabs, active, onTab, children, headerRight }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { items, unread, markRead } = useNotifications();

  return (
    <div className="min-h-screen bg-cream">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <img src="/morningbox-logo.png" alt="MorningBox" className="h-9 w-auto" />
          </button>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button onClick={markRead} className="relative w-10 h-10 rounded-full hover:bg-navy/5 flex items-center justify-center" data-testid="notif-bell">
                  <Bell className="w-5 h-5 text-navy" />
                  {unread > 0 && <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-orange text-white text-[10px] rounded-full flex items-center justify-center">{unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 rounded-2xl p-0 overflow-hidden">
                <div className="p-4 border-b font-semibold text-navy">Notifiche</div>
                <div className="max-h-80 overflow-auto">
                  {items.length === 0 && <p className="p-4 text-sm text-navy/40">Nessuna notifica</p>}
                  {items.map((n) => (
                    <div key={n.id} className="p-4 border-b last:border-0 hover:bg-cream">
                      <p className="text-sm font-medium text-navy">{n.title}</p>
                      <p className="text-xs text-navy/50 mt-0.5">{n.message}</p>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="hidden sm:block text-right mr-1">
              <p className="text-sm font-medium text-navy leading-tight">{user?.name}</p>
              <p className="text-xs text-navy/40 capitalize">{user?.role}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="logout-btn"><LogOut className="w-5 h-5 text-navy" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-navy tracking-tight">{title}</h1>
            {subtitle && <p className="text-navy/50 mt-1">{subtitle}</p>}
          </div>
          {headerRight}
        </div>

        {tabs && (
          <div className="mt-6 flex gap-1 overflow-x-auto pb-1">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => onTab(t.key)} data-testid={`tab-${t.key}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${active === t.key ? "bg-navy text-white" : "text-navy/60 hover:bg-navy/5"}`}>
                {t.label}{typeof t.badge === "number" && t.badge > 0 && <span className="ml-1.5 bg-orange text-white text-[10px] rounded-full px-1.5 py-0.5">{t.badge}</span>}
              </button>
            ))}
          </div>
        )}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-navy/50">{label}</p>
        {Icon && <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent || "bg-cream"}`}><Icon className="w-5 h-5 text-orange" /></div>}
      </div>
      <p className="mt-2 text-2xl font-bold text-navy font-heading" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
    </div>
  );
}

export function EmptyState({ icon: Icon = Coffee, text }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-navy/15 p-12 text-center">
      <Icon className="w-10 h-10 text-navy/20 mx-auto" />
      <p className="mt-3 text-navy/40 text-sm">{text}</p>
    </div>
  );
}
